import React from 'react';
import { FaApple } from 'react-icons/fa';

import { MeridianLogo } from './components/MeridianLogo';
import { config } from './config';

// Windows Logo Icon
const WindowsLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width='14'
    height='14'
    viewBox='0 0 14 14'
    fill='currentColor'
  >
    <path d='M0 2.006L5.713 1.29v5.517H0V2.006zm0 9.988l5.713.716V7.21H0v4.784zm6.355.796L14 14V7.21H6.355v5.58zm0-11.58v5.596H14V0L6.355 1.21z' />
  </svg>
);

/**
 * Decode the Base64 encoded customer name from URL
 */
const decodeCustomerCode = (code: string): string | null => {
  try {
    return atob(code);
  } catch {
    return null;
  }
};

// Get customer name from URL query param (expects Base64 encoded 'c' param)
const getCustomerName = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('c');
  if (!code) return null;
  return decodeCustomerCode(code);
};

const App: React.FC = () => {
  const customerName = getCustomerName();

  // If no customer name provided, show access restricted
  if (!customerName) {
    return (
      <div className='min-h-screen bg-[#081020] relative overflow-hidden font-geist'>
        {/* Background gradient */}
        <div className='absolute bottom-0 left-1/2 -translate-x-1/2 w-[2759px] h-[209px] opacity-60'>
          <div className='w-full h-full bg-gradient-to-t from-[#0d3a6a] via-[#0d1b35] to-transparent blur-3xl' />
        </div>

        {/* Header - Logo */}
        <header className='absolute top-[50px] left-[45px] z-20'>
          <MeridianLogo color='#c9e1ff' />
        </header>

        {/* Main Content - Centered */}
        <div className='min-h-screen flex flex-col items-center justify-center px-6 relative z-10'>
          {/* Restricted Access Card */}
          <div className='bg-gradient-to-b from-[#0d1b35]/80 to-[#0a1529]/80 border border-[#244663]/50 rounded-xl p-12 max-w-[500px] text-center backdrop-blur-sm'>
            {/* Lock Icon */}
            <div className='mb-6 flex justify-center'>
              <div className='w-16 h-16 rounded-full bg-[#244663]/30 border border-[#244663]/50 flex items-center justify-center'>
                <svg
                  width='28'
                  height='28'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <rect
                    x='3'
                    y='11'
                    width='18'
                    height='11'
                    rx='2'
                    stroke='#4b6d94'
                    strokeWidth='2'
                  />
                  <path
                    d='M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11'
                    stroke='#4b6d94'
                    strokeWidth='2'
                    strokeLinecap='round'
                  />
                  <circle cx='12' cy='16' r='1.5' fill='#4b6d94' />
                </svg>
              </div>
            </div>

            <h1 className='text-[#96c9ff] text-[32px] font-light tracking-[0.96px] uppercase mb-4 font-financier leading-[1.1]'>
              Access Restricted
            </h1>
            <p className='text-[#4b6d94] text-[15px] font-light tracking-[0.3px] leading-[1.7] mb-8'>
              This download portal requires a valid access link.
              <br />
              Please use the link provided by your Meridian contact.
            </p>

            {/* Contact CTA */}
            <a
              href='mailto:jonah@meridian.ai'
              className='inline-flex items-center gap-2 px-6 py-3 bg-[#172d58]/60 border border-[#244663] rounded-lg text-[#aacfff] text-[14px] font-medium tracking-[0.3px] hover:bg-[#1e3a6e]/60 hover:border-[#aacfff]/40 transition-all'
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              Contact Sales
            </a>
          </div>
        </div>

        {/* Footer */}
        <footer className='absolute bottom-[30px] right-[45px] z-20'>
          <p className='text-[#aacfff] text-[14px] font-geist font-normal'>
            © 2026 Meridian
          </p>
        </footer>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-[#081020] relative overflow-hidden font-geist'>
      {/* Animation Keyframes */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes subtleGlow {
          0%, 100% {
            text-shadow: 0 0 20px rgba(150, 201, 255, 0.3), 0 0 40px rgba(150, 201, 255, 0.1);
          }
          50% {
            text-shadow: 0 0 30px rgba(150, 201, 255, 0.5), 0 0 60px rgba(150, 201, 255, 0.2);
          }
        }
        @keyframes shimmer {
          0% {
            background-position: -200% center;
          }
          100% {
            background-position: 200% center;
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
          opacity: 0;
        }
        .animate-glow {
          animation: subtleGlow 3s ease-in-out infinite;
        }
        .animate-shimmer {
          background: linear-gradient(
            90deg,
            rgba(150, 201, 255, 0) 0%,
            rgba(150, 201, 255, 0.1) 50%,
            rgba(150, 201, 255, 0) 100%
          );
          background-size: 200% 100%;
          animation: shimmer 3s ease-in-out infinite;
        }
      `}</style>

      {/* Background gradient */}
      <div className='absolute bottom-0 left-1/2 -translate-x-1/2 w-[2759px] h-[209px] opacity-60'>
        <div className='w-full h-full bg-gradient-to-t from-[#0d3a6a] via-[#0d1b35] to-transparent blur-3xl' />
      </div>

      {/* Header - Logo */}
      <header className='absolute top-[50px] left-[45px] z-20'>
        <MeridianLogo color='#c9e1ff' />
      </header>

      {/* Main Content - Centered */}
      <div className='min-h-screen flex flex-col items-center justify-center px-6 relative z-10'>
        {/* Welcome Header */}
        <div
          className='text-center mb-16 animate-fade-in-up'
          style={{ animationDelay: '0.1s' }}
        >
          <h1 className='text-[#96c9ff] text-[42px] font-light tracking-[1.26px] uppercase mb-4 font-financier leading-[1.1] animate-glow'>
            Welcome to Meridian, {customerName}
          </h1>
          <p className='text-[#aacfff] text-[16px] font-light tracking-[0.3px] leading-[1.6]'>
            Download the Meridian desktop app to get started.
          </p>
        </div>

        {/* Download Buttons */}
        <div className='flex gap-8 items-start justify-center flex-wrap'>
          {/* Mac Download */}
          <div
            className='flex flex-col gap-3 items-center animate-fade-in-up'
            style={{ animationDelay: '0.3s' }}
          >
            <a
              href={`${config.VITE_BACKEND_URL}/downloads/macos`}
              className='w-[268px] h-[48px] relative rounded-full border border-[#244663]/50 flex items-center justify-center group'
            >
              <div className='absolute inset-[1px] rounded-full bg-[#081020] shadow-[0px_8px_10px_0px_rgba(9,16,31,0.25),0px_4px_4px_0px_#09101f,inset_0px_-2px_8px_0px_rgba(91,121,208,0.5)] flex items-center justify-center gap-1.5 overflow-hidden transition-all duration-300 group-hover:bg-[#0a1428] group-hover:shadow-[0px_8px_10px_0px_rgba(9,16,31,0.25),0px_4px_4px_0px_#09101f,inset_0px_-2px_12px_0px_rgba(91,121,208,0.7)]'>
                <FaApple
                  className='text-[#aacfff] transition-transform duration-300 group-hover:scale-110'
                  size={17}
                />
                <span className='text-[15px] font-light text-[#aacfff] tracking-[0.3px] leading-[1.6]'>
                  Download for Mac
                </span>
              </div>
              <div className='absolute inset-0 rounded-full animate-shimmer pointer-events-none' />
            </a>
            <span className='text-[14px] font-normal text-[#aacfff] opacity-70 leading-[1.6]'>
              Versions 13.0 and later
            </span>
          </div>

          {/* Windows Download */}
          <div
            className='flex flex-col gap-3 items-center animate-fade-in-up'
            style={{ animationDelay: '0.45s' }}
          >
            <a
              href={`${config.VITE_BACKEND_URL}/downloads/windows`}
              className='w-[268px] h-[48px] relative rounded-full border border-[#244663]/50 flex items-center justify-center group'
            >
              <div className='absolute inset-[1px] rounded-full bg-[#081020] shadow-[0px_8px_10px_0px_rgba(9,16,31,0.25),0px_4px_4px_0px_#09101f,inset_0px_-2px_8px_0px_rgba(91,121,208,0.5)] flex items-center justify-center gap-1.5 overflow-hidden transition-all duration-300 group-hover:bg-[#0a1428] group-hover:shadow-[0px_8px_10px_0px_rgba(9,16,31,0.25),0px_4px_4px_0px_#09101f,inset_0px_-2px_12px_0px_rgba(91,121,208,0.7)]'>
                <WindowsLogo className='text-[#aacfff] transition-transform duration-300 group-hover:scale-110' />
                <span className='text-[15px] font-light text-[#aacfff] tracking-[0.3px] leading-[1.6]'>
                  Download for Windows
                </span>
              </div>
              <div className='absolute inset-0 rounded-full animate-shimmer pointer-events-none' />
            </a>
            <span className='text-[14px] font-normal text-[#aacfff] opacity-70 leading-[1.6]'>
              Versions 64.03 and later
            </span>
          </div>
        </div>

        {/* Support Section */}
        <div
          className='mt-16 text-center animate-fade-in-up'
          style={{ animationDelay: '0.6s' }}
        >
          <p className='text-[#4b6d94] text-[14px] font-geist font-normal'>
            Questions or issues?{' '}
            <a
              href='mailto:support@meridian.ai'
              className='text-[#aacfff] hover:text-[#96c9ff] transition-colors'
            >
              support@meridian.ai
            </a>
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className='absolute bottom-[30px] right-[45px] z-20'>
        <p className='text-[#aacfff] text-[14px] font-geist font-normal'>
          © 2026 Meridian
        </p>
      </footer>
    </div>
  );
};

export default App;
